import { Space, Tag } from "antd";

import { FALLBACK_PLATFORM_TAG_COLOR, PLATFORM_METADATA_KEY, PLATFORM_TAG_COLOR_MAP } from "../helper";

/**
 * Renders a mapping's metadata as compact chips so a row shows its extra fields
 * without opening the metadata modal.
 *
 * A null, empty or empty-list value renders no chip. A `platform` value is a
 * coloured chip with a readable name, a number is `label: value` with
 * thousands separators, and anything else is a neutral `label: value` chip.
 * Renders a dash when no field holds a value.
 *
 * @param {object} props
 * @param {Record<string, any>} [props.metadata] - The row's metadata, keyed by field name.
 * @param {Array<{name: string, label: string}>} [props.metadataFields] - Field list of the row's
 *   type. A field missing from it is labelled from its own key.
 */
const MetadataChips = ({ metadata = {}, metadataFields = [] }) => {
  const fieldNameToLabelMap = Object.fromEntries(metadataFields.map(({ name, label }) => [name, label]));

  const chips = Object.entries(metadata ?? {})
    .filter(([, value]) => !isEmptyMetadataValue(value))
    .map(([name, value]) => {
      if (name === PLATFORM_METADATA_KEY) {
        return (
          <Tag key={name} color={PLATFORM_TAG_COLOR_MAP[value] ?? FALLBACK_PLATFORM_TAG_COLOR}>
            {toPlatformLabel(value)}
          </Tag>
        );
      }

      const label = fieldNameToLabelMap[name] ?? toLabel(name);
      return <Tag key={name}>{`${label}: ${toDisplayValue(value)}`}</Tag>;
    });

  if (chips.length === 0) {
    return <span>--</span>;
  }

  return (
    <Space size={[4, 4]} wrap>
      {chips}
    </Space>
  );
};

/**
 * Whether a metadata value carries nothing worth a chip. `false` and `0` are values.
 *
 * @param {any} value
 * @returns {boolean}
 */
const isEmptyMetadataValue = (value) =>
  value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0);

/**
 * Turns a stored platform value into its display name, `CODEFORCES_PROBLEM` into `Codeforces`.
 *
 * @param {string} platform
 * @returns {string}
 */
const toPlatformLabel = (platform) =>
  String(platform)
    .replace(/_PROBLEM$/, "")
    .toLowerCase()
    .replace(/(^|_)([a-z])/g, (_, separator, letter) => `${separator ? " " : ""}${letter.toUpperCase()}`);

/**
 * Turns a camelCase field name into a heading, `problemCount` into `Problem count`.
 *
 * @param {string} name
 * @returns {string}
 */
const toLabel = (name) => {
  const spaced = name.replace(/([A-Z])/g, " $1").toLowerCase();
  return `${spaced.charAt(0).toUpperCase()}${spaced.slice(1)}`;
};

/**
 * Formats a metadata value for a chip: numbers with separators, booleans as
 * Yes or No, lists comma-joined.
 *
 * @param {any} value
 * @returns {string}
 */
const toDisplayValue = (value) => {
  if (typeof value === "number") {
    return value.toLocaleString();
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  return String(value);
};

export default MetadataChips;
